import { ArrowLeft, Calendar, Clock, Share2 } from 'lucide-react';
import { marked } from 'marked';
import { useEffect } from 'react';
import { updatePageMeta, addStructuredData, generateBlogPostStructuredData } from '../../utils/seo';

marked.setOptions({
  breaks: true,
  gfm: true,
  headerIds: true,
  mangle: false,
  sanitize: false,
});

const post = {
  "slug": "mothers-fathers-day-gift-ideas",
  "title": "Mother's and Father's Day: Making Parents Feel Special",
  "excerpt": "Thoughtful gift ideas for honoring parents. From sentimental to practical, find the perfect way to say thank you.",
  "date": "2025-05-01",
  "readTime": "7 min read",
  "category": "Festivals",
  "image": "👨‍👩‍👧‍👦",
  "heroImage": "https://images.unsplash.com/photo-1490578474895-699cd4e2cf59?w=1200&h=600&fit=crop",
  "author": "SwapGifts Editorial Team",
  "content": "# Mother's and Father's Day: Making Parents Feel Special\n\nHonoring parents doesn't require expensive gifts - it's about showing appreciation and love. Here's how to make these days memorable.\n\n## Mother's Day Gift Ideas\n\n**Sentimental Gifts**:\n- Photo album or digital frame with family photos\n- Custom jewelry with birthstones or engraving\n- Handwritten letter expressing gratitude\n- Recipe book with family favorites\n- Ancestry DNA kit\n\n**Pampering Gifts**:\n- Spa day or massage gift certificate\n- Luxury skincare or bath products\n- Cozy robe and slippers\n- Subscription box (beauty, book, or hobby-related)\n\n**Practical Gifts**:\n- Kitchen gadgets (air fryer, stand mixer, espresso machine)\n- Smart home devices\n- Gardening tools or plant subscription\n- Quality cookware or serving pieces\n\n**Experience Gifts**:\n- Brunch or dinner at her favorite restaurant\n- Concert or theater tickets\n- Cooking or art class together\n- Weekend getaway\n- Photography session\n\n## Father's Day Gift Ideas\n\n**For the Hobbyist**:\n- Golf accessories\n- Grilling tools and gadgets\n- Fishing gear\n- Workshop tools\n- Sports memorabilia\n\n**Tech and Gadgets**:\n- Wireless headphones or earbuds\n- Smart watch or fitness tracker\n- Tablet or e-reader\n- Portable Bluetooth speaker\n- Phone accessories\n\n**Food and Drink**:\n- Craft beer or whiskey tasting set\n- Gourmet BBQ sauce collection\n- Coffee subscription\n- Steak box delivery\n- Personalized cutting board\n\n**Experience Gifts**:\n- Sporting event tickets\n- Brewery or distillery tour\n- Golf outing\n- Fishing charter\n- Car detailing service\n\n## Budget-Friendly Options\n\nUnder $30:\n- Homemade coupon book (car washes, yard work, dinner)\n- Framed family photo\n- Their favorite treats or coffee\n- Personalized mug\n- Books by favorite authors\n- Plants or flowers\n- Puzzle or board game\n\n## DIY Gift Ideas\n\n**From Kids**:\n- Handprint or footprint art\n- Painted flower pots with plants\n- Homemade cards and crafts\n- Breakfast in bed (with adult supervision)\n- Video compilation of messages\n\n**From Adult Children**:\n- Compiled family recipe book\n- Photo collage or scrapbook\n- Garden stepping stones\n- Handmade woodwork or crafts\n- Baked goods or preserves\n\n## Making the Day Special\n\nBeyond gifts:\n- Quality time together\n- Cook their favorite meal\n- Take care of chores they usually do\n- Plan a family gathering\n- Share favorite memories\n- Call if you can't be there in person\n\n## For Large Families\n\nWhen siblings want to give a joint gift:\n\n**Collaboration Ideas**:\n- Big-ticket item (new TV, furniture, vacation)\n- Home improvement project\n- Professional services (landscaping, cleaning)\n- Yearly subscription to something they love\n\n**Coordination Tips**:\n- Start planning 4-6 weeks ahead\n- Use SwapGifts to organize contributions\n- Set clear budget per person\n- Designate one person to handle purchase\n- Create a nice reveal presentation\n\n## Conclusion\n\nThe best gifts show that you know and appreciate your parents. Whether it's big or small, expensive or homemade, what matters is the thought and love behind it."
};

export default function MothersFathersDayGiftIdeasPage() {
  useEffect(() => {
    updatePageMeta({
      title: `${post.title} | SwapGifts Blog`,
      description: post.excerpt,
      ogTitle: post.title,
      ogDescription: post.excerpt,
      ogImage: post.heroImage || 'https://swapgifts.store/default-blog-image.jpg',
      canonicalUrl: `https://swapgifts.store/blog/${post.slug}`,
      keywords: `gift exchange, ${post.category.toLowerCase()}, secret santa, ${post.title.toLowerCase()}`
    });

    addStructuredData(generateBlogPostStructuredData({
      title: post.title,
      excerpt: post.excerpt,
      date: post.date,
      author: post.author || 'SwapGifts Editorial Team',
      heroImage: post.heroImage,
      slug: post.slug
    }));
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <a
          href="/blog"
          className="inline-flex items-center gap-2 text-green-600 font-medium hover:underline mb-8"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Blog
        </a>

        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          {post.heroImage && (
            <div className="w-full h-96 overflow-hidden">
              <img
                src={post.heroImage}
                alt={post.title}
                className="w-full h-full object-cover"
              />
            </div>
          )}

          <div className="p-8 md:p-12">
            <div className="flex items-center gap-4 text-sm text-gray-500 mb-4">
              <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full font-medium">
                {post.category}
              </span>
              <div className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                {post.readTime}
              </div>
              <div className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                {new Date(post.date).toLocaleDateString('en-US', {
                  month: 'long',
                  day: 'numeric',
                  year: 'numeric'
                })}
              </div>
            </div>

            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4 leading-tight">
              {post.title}
            </h1>

            {post.author && (
              <p className="text-sm text-gray-500 mb-6">
                By <span className="font-medium text-gray-700">{post.author}</span>
              </p>
            )}

            <p className="text-xl text-gray-600 mb-8 leading-relaxed border-b border-gray-200 pb-8">
              {post.excerpt}
            </p>

            <div className="flex items-center gap-4 pb-8 mb-8 border-b border-gray-200">
              <button className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors">
                <Share2 className="w-4 h-4" />
                Share
              </button>
            </div>

            <div
              className="prose prose-lg max-w-none
                prose-headings:font-bold prose-headings:text-gray-900 prose-headings:mt-8 prose-headings:mb-4
                prose-h2:text-3xl prose-h2:mt-12 prose-h2:mb-6
                prose-h3:text-2xl prose-h3:mt-8 prose-h3:mb-4
                prose-p:text-gray-700 prose-p:leading-relaxed prose-p:mb-6
                prose-a:text-green-600 prose-a:no-underline hover:prose-a:underline
                prose-strong:text-gray-900 prose-strong:font-semibold
                prose-ul:my-6 prose-ul:space-y-2
                prose-ol:my-6 prose-ol:space-y-2
                prose-li:text-gray-700 prose-li:leading-relaxed
                prose-code:bg-gray-100 prose-code:px-2 prose-code:py-1 prose-code:rounded prose-code:text-sm"
              dangerouslySetInnerHTML={{ __html: marked.parse(post.content) }}
            />
          </div>

          <div className="mt-12 bg-gradient-to-r from-green-600 to-blue-600 rounded-lg p-8 text-white text-center">
            <h2 className="text-2xl font-bold mb-4">Ready to organize your gift exchange?</h2>
            <p className="text-green-100 mb-6">
              Put these tips into action and create your exchange in minutes
            </p>
            <a
              href="/create"
              className="inline-block px-8 py-3 bg-white text-green-600 font-semibold rounded-lg hover:bg-gray-100 transition-colors"
            >
              Start Drawing Names
            </a>
          </div>
        </div>
      </article>
    </div>
  );
}
